import api from '@disco/api/server'
import {
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  TEAM_INBOX,
} from '@disco/constants/GroupTypes'
import {
  getGroup,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { capitalizeFirstLetter } from '@disco/utils/string-utils'
import {
  convertUserDropdownObjectToLocked,
  convertUserDropdownObjectToUnlocked,
  getUserDropdownObject,
} from '@disco/utils/user'
import cn from 'clsx'
import get from 'lodash/get'
import * as React from 'react'
import { connect } from 'react-redux'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { Group, GroupId } from 'types/group'
import type { User, UserId } from 'types/user'
import {
  createGroup,
  deleteGroup,
  loadGroup,
  loadGroups,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import RichTextEditor from 'components/common/rich-text-editor'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import UserSelector from 'components/common/user-selector'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'
import TextField from 'components/ui/text-field'
import type { RootState } from 'store/configureStore'
import type { ClearableDropdownOption } from 'types/ui'
const mapDispatchToProps = {
  loadGroups,
  updateGroup,
  createGroup,
  deleteGroup,
  loadGroup,
}
type Props = {
  id?: number
  internal_name?: string
  name: string
  managers?: number[]
  members?: number[]
  hidden?: boolean
  bulk_upload?: boolean
  user: User
  groupType: string
  parentGroup?: Record<string, any>
  onSubmit: (...args: Array<any>) => any
  onClose: (...args: Array<any>) => any
  onDelete?: (...args: Array<any>) => any
  validate: (...args: Array<any>) => any
  deleteGroup: (...args: Array<any>) => any
  updateGroup: (...args: Array<any>) => any
  createGroup: (...args: Array<any>) => any
  loadGroup: (arg0: GroupId) => Promise<any>
  getValidationMessages: (...args: Array<any>) => any
  isFetching: boolean
  disableDestructiveActions: boolean
  isSubfolder: boolean
}

/**
 * Comprehensive form component for creating and editing custom libraries (channels/inboxes/folders)
 * Handles user management, permissions, bulk upload settings, and custom messaging
 * Supports validation, folder nesting, and different group types with appropriate UI variations
 * @param id - Group ID for editing existing group
 * @param internal_name - Internal name for the group
 * @param name - Display name for the group
 * @param managers - Array of manager user IDs
 * @param members - Array of member user IDs
 * @param hidden - Whether the group is hidden
 * @param bulk_upload - Whether bulk uploads are allowed
 * @param user - Current user object
 * @param groupType - Type of group being created/edited
 * @param parentGroup - Parent group for folder creation
 * @param onSubmit - Callback when form is successfully submitted
 * @param onClose - Callback to close the form
 * @param onDelete - Callback for deletion operations
 * @param validate - Form validation function
 * @param deleteGroup - Redux action to delete group
 * @param updateGroup - Redux action to update group
 * @param createGroup - Redux action to create group
 * @param loadGroup - Redux action to load group data
 * @param getValidationMessages - Function to get validation messages
 * @param isFetching - Whether group data is being fetched
 * @param disableDestructiveActions - Whether destructive actions are disabled
 * @param isSubfolder - Whether this is a subfolder
 */
class EditCustomLibraryForm extends React.Component<
  Props,
  {
    name: string
    bulkUpload: boolean
    hidden?: boolean
    customMessage: Record<string, any> | null | undefined
    //customMessage - draftjs
    customMessageHtml: string | null | undefined
    businessUsers: User[]
    businessUsersLoaded: boolean
    allUsersAreMembers: boolean
    chosenMembers: ClearableDropdownOption[]
    hiddenMembers: UserId[]
    chosenManagers: ClearableDropdownOption[]
    hiddenManagers: UserId[]
    submitting: boolean
  }
> {
  validatorTypes: any
  _richText: React.Component<any, any> | null | undefined

  constructor(props: Props) {
    super(props)
    this.state = {
      name:
        props.internal_name && props.internal_name.length
          ? props.internal_name
          : props.name,
      bulkUpload: props.bulk_upload || false,
      hidden: props.hidden,
      customMessage: get(props, 'meta.custom_message', null),
      customMessageHtml: get(props, 'meta.custom_message_html', null),
      businessUsers: [],
      businessUsersLoaded: false,
      allUsersAreMembers: !props.members || props.members.length === 0,
      chosenMembers: [],
      hiddenMembers: [],
      chosenManagers: [],
      hiddenManagers: [],
      submitting: false,
    }
    this.validatorTypes = strategy.createSchema({
      name: 'required',
      chosenManagers: !props.parentGroup
        ? 'required|array|min:1'
        : 'array|min:0',
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!nextProps.isFetching && this.props.isFetching) {
      const customMessage = get(nextProps, 'meta.custom_message', null)
      this.setState({
        name:
          nextProps.internal_name && nextProps.internal_name.length
            ? nextProps.internal_name
            : nextProps.name,
        bulkUpload: nextProps.bulk_upload || false,
        hidden: nextProps.hidden,
        customMessage: customMessage,
        customMessageHtml: get(nextProps, 'meta.custom_message_html', null),
        allUsersAreMembers:
          !nextProps.members || nextProps.members.length === 0,
      })
      if (customMessage && this._richText) {
        // @ts-expect-error
        this._richText.setEditorContent(customMessage)
      }
    }
  }

  getValidatorData = () => {
    return {
      name: this.state.name,
      chosenManagers: [
        ...this.state.hiddenManagers,
        ...this.state.chosenManagers,
      ],
    }
  }
  renderHelpText = message => {
    return <div className="warning-block">{message}</div>
  }
  _onCustomMessageChange = (customMessage, customMessageHtml) => {
    this.setState({
      customMessage,
      customMessageHtml,
    })
  }

  componentDidMount() {
    const isFolder = this.props.parentGroup
    const currentUser = this.props.user
    const promise = this.props.id
      ? Promise.all([
          this.props.loadGroup(this.props.id),
          api.loadBusinessUsers(),
        ])
      : Promise.all([null, api.loadBusinessUsers()])
    promise.then(([groupResponse, users]) => {
      const normalizedUsers = users.map(u => u.user)
      const group = groupResponse ? groupResponse.group : {}
      const { members, managers } = group
      // DISCO staff users should be hidden from visible managers
      let hiddenManagers = []
      let chosenManagers = managers
        ? managers.reduce((accumulator, managerId) => {
            let result = [...accumulator]
            const manager = normalizedUsers.find(user => user.id === managerId)

            if (!manager) {
              hiddenManagers.push(managerId)
            } else {
              const locked = manager.id === currentUser.id
              result.push(getUserDropdownObject(manager, locked))
            }

            return result
          }, [])
        : []
      // DISCO staff users should be hidden from visible members
      let hiddenMembers = members ? [] : hiddenManagers
      let chosenMembers = members
        ? members.reduce((accumulator, memberId) => {
            let result = [...accumulator]
            const member = normalizedUsers.find(user => user.id === memberId)

            if (!member) {
              hiddenMembers.push(memberId)
            } else {
              const userBelongToManagers = managers.includes(memberId)
              result.push(getUserDropdownObject(member, userBelongToManagers))
            }

            return result
          }, [])
        : chosenManagers

      // Add user as manager if managers are empty
      if (!isFolder && chosenManagers.length === 0) {
        chosenManagers = [getUserDropdownObject(currentUser, true)]
      }

      const sortedUsers = normalizedUsers.sort((a, b) => {
        if (`${a.first_name}${a.last_name}` > `${b.first_name}${b.last_name}`) {
          return 1
        }

        if (`${a.first_name}${a.last_name}` < `${b.first_name}${b.last_name}`) {
          return -1
        }

        return 0
      })
      this.setState({
        businessUsers: sortedUsers,
        // TODO: replace `state.businessUsersLoaded` with `props.businessUsers.loaded` (reducer)
        businessUsersLoaded: true,
        chosenManagers,
        hiddenManagers,
        chosenMembers,
        hiddenMembers,
        // TODO: remove `allUsersAreMembers` re-calc by moving loading outside
        allUsersAreMembers: !chosenMembers || chosenMembers.length === 0,
        customMessage: get(group, 'meta.custom_message', null),
        customMessageHtml: get(group, 'meta.custom_message_html', null),
      })
    })
  }

  _onMemberSelect = value => {
    this.setState({
      chosenMembers: value,
    })
  }
  _onManagerSelect = value => {
    this.setState({
      chosenManagers: value,
    })
    const managersNotIncludedToMembersList = value.filter(manager => {
      return !this.state.chosenMembers.some(member => {
        return member.value === manager.value
      })
    })
    let chosenMembers = this.state.chosenMembers

    if (managersNotIncludedToMembersList.length > -1) {
      chosenMembers = [
        ...this.state.chosenMembers,
        ...managersNotIncludedToMembersList,
      ]
    }

    const updatedUsers = chosenMembers.map(member => {
      const lockedUser = value.some(manager => manager.value === member.value)
      return lockedUser
        ? convertUserDropdownObjectToLocked(member)
        : convertUserDropdownObjectToUnlocked(member)
    })
    this.setState({
      chosenMembers: updatedUsers,
    })
  }
  _onNameChange = e => {
    this.setState({
      name: e.target.value,
    })
  }
  _onBulkUploadChange = () => {
    this.setState({
      bulkUpload: !this.state.bulkUpload,
    })
  }
  _handleInboxVisibilityChange = () => {
    this.setState({
      hidden: !this.state.hidden,
    })
  }
  _onMembersSwitch = () => {
    this.setState({
      allUsersAreMembers: !this.state.allUsersAreMembers,
      chosenMembers:
        this.state.allUsersAreMembers &&
        this.state.businessUsersLoaded &&
        this.state.chosenMembers.length === 0
          ? this.state.chosenManagers.map(m =>
              convertUserDropdownObjectToLocked(m),
            )
          : this.state.chosenMembers,
      hiddenMembers:
        this.state.allUsersAreMembers &&
        this.state.businessUsersLoaded &&
        this.state.hiddenMembers.length === 0
          ? this.state.hiddenManagers
          : this.state.hiddenMembers,
    })
  }
  _onValidate = error => {
    if (error) return
    const members = this.state.allUsersAreMembers
      ? []
      : [
          ...this.state.hiddenMembers,
          ...this.state.chosenMembers.map(member => member.value),
        ]
    const managers = [
      ...this.state.hiddenManagers,
      ...this.state.chosenManagers.map(manager => manager.value),
    ]
    const updateTemplate = {
      parent: this.props.parentGroup ? this.props.parentGroup.id : null,
      bulk_upload: this.state.bulkUpload,
      members,
      managers,
      meta: this.state.customMessage
        ? {
            custom_message: this.state.customMessage,
            custom_message_html: this.state.customMessageHtml,
          }
        : null,
    }

    if (this.props.id) {
      const updateData = {
        id: this.props.id,
        hidden: this.state.hidden,
        name: undefined,
        internal_name: undefined,
        ...updateTemplate,
      }

      if (this.props.internal_name && this.props.internal_name.length) {
        updateData.internal_name = this.state.name
      } else {
        updateData.name = this.state.name
      }

      this.props.updateGroup(updateData)
      this.props.onSubmit(this.state.name)
    } else {
      const createData = {
        name: this.state.name,
        group_type: this.props.groupType,
        ...updateTemplate,
      }
      this.setState({
        submitting: true,
      })
      this.props.createGroup(createData).then(group => {
        this.setState({
          submitting: false,
        })
        return this.props.onSubmit(this.state.name, group)
      })
    }
  }
  onSubmit = event => {
    event.preventDefault()
    this.props.validate(this._onValidate)
  }

  render() {
    let formCaption = null
    let submitCaption = null
    let submittingCaption = null
    let nameCaption = null
    const inboxName =
      this.props.internal_name && this.props.internal_name.length
        ? this.props.internal_name
        : this.props.name

    if (this.props.parentGroup) {
      const folderType =
        this.props.parentGroup.parent || this.props.isSubfolder
          ? 'subfolder'
          : 'folder'
      formCaption = this.props.id
        ? `Edit ${folderType}`
        : `Create new ${folderType}`
      submitCaption = this.props.id ? `Update` : `Create`
      submittingCaption = this.props.id ? `Updating` : `Creating`
      nameCaption = `${capitalizeFirstLetter(folderType)} name`
    } else {
      const channelName =
        this.props.groupType === CUSTOM_CHANNEL ? 'channel' : 'inbox'

      if (
        (this.props.groupType === CUSTOM_INBOX ||
          this.props.groupType === TEAM_INBOX) &&
        this.props.id
      ) {
        submitCaption = 'Save'
        submittingCaption = 'Saving'
        formCaption = `${inboxName} Settings`
      } else {
        submitCaption = this.props.id ? 'Rename' : `Create`
        submittingCaption = this.props.id ? 'Renaming' : `Creating`
        formCaption = this.props.id
          ? `Rename ${channelName}`
          : `Create new ${channelName}`
      }

      nameCaption =
        this.props.groupType === CUSTOM_CHANNEL ? 'Channel name' : 'Inbox name'
    }

    const nameMessage = this.props.getValidationMessages('name').length
      ? 'Name is required'
      : null
    const managersMessage = this.props.getValidationMessages('chosenManagers')
      .length
      ? 'Please add at least one manager'
      : null
    const isFolder = this.props.parentGroup
    const membershipControls =
      this.props.groupType === CUSTOM_INBOX && !isFolder ? (
        <React.Fragment>
          <Row columnsSize={[12]} marginBottom="small">
            <div>
              <Label
                caption="Managers"
                tooltipId="tooltip_settings_channels_managers"
              />
              <UserSelector
                placeholder="Choose managers"
                users={this.state.businessUsers}
                chosenUsers={this.state.chosenManagers}
                usersLoading={!this.state.businessUsersLoaded}
                onUserSelect={this._onManagerSelect}
              />
              {managersMessage && this.renderHelpText(managersMessage)}
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <Label caption="Members" tooltipId="tooltip_inbox_members" />
              <UserSelector
                placeholder="Choose members"
                users={this.state.businessUsers}
                chosenUsers={
                  this.state.allUsersAreMembers ? [] : this.state.chosenMembers
                }
                usersLoading={!this.state.businessUsersLoaded}
                onUserSelect={this._onMemberSelect}
                disabled={this.state.allUsersAreMembers}
              />
              <Checkbox
                label="Visible to all users"
                checked={this.state.allUsersAreMembers}
                onChange={this._onMembersSwitch}
              />
            </div>
          </Row>
        </React.Fragment>
      ) : null
    const customInbox =
      [CUSTOM_INBOX, TEAM_INBOX].indexOf(this.props.groupType) > -1
    const formClass = cn(null, {
      Form_Channel: this.props.groupType === CUSTOM_CHANNEL,
      Form_CustomInbox: customInbox,
    })
    const editingDisabled =
      !this.state.chosenManagers ||
      this.state.chosenManagers.every(m => m.value !== this.props.user.id)
    const canHideInbox =
      this.props.groupType === TEAM_INBOX ? !editingDisabled : false
    const allowBulkUploads =
      this.props.groupType !== CUSTOM_CHANNEL ? (
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <div>
              <Checkbox
                label={
                  <React.Fragment>
                    Allow bulk uploads?{' '}
                    <HelpTooltip
                      id={
                        this.props.groupType === CUSTOM_CHANNEL
                          ? 'tooltip_settings_channels_allow_bulk_uploads'
                          : 'tooltip_allow_bulk_uploads'
                      }
                    />
                  </React.Fragment>
                }
                checked={this.state.bulkUpload}
                onChange={this._onBulkUploadChange}
              />
            </div>
            {canHideInbox ? (
              <div>
                <Checkbox
                  label="Let other DISCO businesses find my team inbox and send to me"
                  checked={!this.state.hidden}
                  onChange={this._handleInboxVisibilityChange}
                />
              </div>
            ) : null}
          </div>
        </Row>
      ) : null
    const addCustomMessage = customInbox ? (
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <Label caption="Add custom message" />
          <RichTextEditor
            ref={r => {
              this._richText = r
            }}
            className="CampaignDetails_Input"
            initialContent={this.state.customMessage}
            onChange={this._onCustomMessageChange}
          />
          <div className="Form_FieldNote">
            The custom message is displayed on your public upload page. You can
            use it to list instructions for people who send you files – like the
            kind of metadata you’d like, preferred file types or anything else.
          </div>
        </div>
      </Row>
    ) : null
    return (
      <ModalWindow
        noPaddingBottom
        noMinHeight
        header={<Heading>{formCaption}</Heading>}
        actions={
          <ButtonContainer>
            <Button caption="Cancel" onClick={this.props.onClose} />
            <Button
              appearance="primary"
              caption={submitCaption}
              onClick={this.onSubmit}
              progressCaption={submittingCaption}
              progressIndicator={this.state.submitting}
            />
          </ButtonContainer>
        }
        onClose={this.props.onClose}
        width={this.props.groupType === CUSTOM_INBOX ? 'medium' : 'small'}
        isOpen
      >
        <div className={formClass}>
          {!this.state.businessUsersLoaded ? (
            <Spinner />
          ) : (
            <>
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <TextField
                    disabled={this.props.disableDestructiveActions}
                    label={nameCaption}
                    value={this.state.name}
                    onChange={this._onNameChange}
                    placeholder="Enter name"
                  />
                  {nameMessage && this.renderHelpText(nameMessage)}
                </div>
              </Row>
              {membershipControls}
              {allowBulkUploads}
              {addCustomMessage}
            </>
          )}
        </div>
      </ModalWindow>
    )
  }
}

export default connect(
  (
    state: RootState,
    { id, parentGroup }: { id: GroupId; parentGroup: Group },
  ) => {
    const getGroupLocks = makeGetGroupLocks()
    return {
      disableDestructiveActions: getGroupLocks(state, {
        groupId: id,
      })?.lock_structure,
      user: state.user,
      isSubfolder:
        parentGroup &&
        !!getGroup(state, {
          groupId: parentGroup.id,
        })?.parent,
    }
  },
  mapDispatchToProps,
)(validation(strategy)(EditCustomLibraryForm)) as any
